/* eslint-disable react/no-unescaped-entities */
import { Card } from '@/components/ui/card';
import { Code2, Zap } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const customInstallSnippet = (store) => `<!-- External Checkout — Site custom / IA (v7) · à coller UNE fois avant </body> sur toutes les pages -->
<script src="${BACKEND_URL}/api/snippet.js?v=7&store=${store.id}"
        data-store-id="${store.id}"
        defer></script>`;

export const customAttributesSnippet = `<!-- 1) Carte produit : le snippet lit le nom, le prix et l'image dans la carte -->
<div data-ec-product data-ec-sku="BOUGIE-01">
  <img src="/img/bougie.jpg" alt="Bougie parfumée">
  <h3 data-ec-name>Bougie parfumée</h3>
  <span data-ec-price>19,90 €</span>
  <input type="number" value="1" min="1" data-ec-qty>
  <button data-ec-add>Ajouter au panier</button>
  <button data-ec-buy>Acheter maintenant</button>
</div>

<!-- 2) Ou tout dans les attributs du bouton (n'importe où dans la page) -->
<button data-ec-buy
        data-ec-name="Coffret duo"
        data-ec-price="39.80"
        data-ec-image="https://mon-site.fr/img/coffret.jpg"
        data-ec-sku="COFFRET-DUO"
        data-ec-qty="1">Acheter — 39,80 €</button>

<!-- 3) Page produit avec JSON-LD / OpenGraph : un simple data-ec-buy suffit,
        le nom, le prix et l'image sont détectés automatiquement -->
<button data-ec-buy>Acheter</button>

<!-- 4) Bouton de votre panier : envoie tous les articles ajoutés via data-ec-add -->
<button data-ec-checkout>Payer ma commande</button>`;

export const customJsSnippet = `<script>
  // Ajouter au panier du snippet (persistant entre les pages)
  externalCheckout.add({ name: 'Huile visage', price: 39.8, image: 'https://mon-site.fr/img/huile.jpg', sku: 'HUILE-30', qty: 1 });

  // Suivre le panier pour mettre à jour votre compteur
  window.addEventListener('externalcheckout:cart', (e) => console.log(e.detail.items));

  // Lancer le paiement du panier…
  externalCheckout.checkout();

  // …ou envoyer directement une liste d'articles
  externalCheckout.go([
    { name: 'Sérum éclat 30ml', price: 49.9, image: 'https://mon-site.fr/img/serum.jpg', qty: 2 },
    { name: 'Masque argile', price: 19.9, qty: 1 },
  ]);
</script>`;

export function CustomSiteGuide({ store, CopyBlock }) {
  const isAi = store.platform === 'ai';
  return (
    <Card className="card-flat p-6" data-testid="snippet-custom-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-md bg-emerald-600 text-white flex items-center justify-center shrink-0"><Code2 className="h-5 w-5" /></div>
        <div className="flex-1">
          <h2 className="font-display text-xl font-bold tracking-tight">{isAi ? 'Site IA' : 'Site custom'} · {store.name}</h2>
          <p className="text-sm text-zinc-600 mt-1">
            Aucune API à connecter : le script récupère <strong>le nom, le prix, l'image et la quantité</strong> de chaque produit directement dans vos pages,
            garde le panier entre les pages et envoie le tout au checkout. Chaque produit vu est créé automatiquement dans <strong>Produits</strong> (prix et image compris)
            et les ventes remontent dans Transactions, Clients et BatCycle.
          </p>
        </div>
      </div>

      <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-4 mb-5" data-testid="snippet-custom-steps">
        <div className="overline mb-3">— Installation en 3 étapes</div>
        <ol className="space-y-2.5">
          {[
            <>Collez le <b>script d'installation</b> ci-dessous juste avant <code className="font-mono bg-white border border-zinc-200 px-1 rounded text-xs">&lt;/body&gt;</code> de toutes vos pages (template / layout / footer).</>,
            <>Sur vos boutons d'achat, ajoutez <code className="font-mono bg-white border border-zinc-200 px-1 rounded text-xs">data-ec-buy</code> (achat immédiat) ou <code className="font-mono bg-white border border-zinc-200 px-1 rounded text-xs">data-ec-add</code> + un bouton <code className="font-mono bg-white border border-zinc-200 px-1 rounded text-xs">data-ec-checkout</code> (panier). Le prix et l'image sont lus dans la carte produit, les attributs <code className="font-mono bg-white border border-zinc-200 px-1 rounded text-xs">data-ec-*</code> ou le JSON-LD / OpenGraph de la page.</>,
            <>Testez un achat : le produit apparaît dans <b>Produits</b> avec son prix et son image. Le prix enregistré fait ensuite foi (le site ne peut pas le modifier), sauf si vous activez « Synchroniser les prix depuis le site » sur la boutique.</>,
          ].map((txt, i) => (
            <li key={i} className="flex gap-3 text-sm text-zinc-700"><span className="shrink-0 h-6 w-6 rounded-full bg-zinc-900 text-white text-xs font-bold grid place-items-center">{i + 1}</span><span className="leading-relaxed">{txt}</span></li>
          ))}
        </ol>
      </div>

      <div className="space-y-5">
        <CopyBlock title="1 · Script d'installation" hint="Une seule fois, sur toutes les pages." code={customInstallSnippet(store)} testId="snippet-custom-install" />
        <CopyBlock title="2 · Attributs HTML sur vos produits" hint="Quatre façons de déclencher le paiement : carte produit, attributs sur le bouton, détection automatique (JSON-LD / OpenGraph) ou bouton panier." code={customAttributesSnippet} testId="snippet-custom-attrs" />
        <CopyBlock title="3 · API JavaScript (optionnel)" hint="Pour les sites qui gèrent déjà leur propre panier en JS." code={customJsSnippet} testId="snippet-custom-js" />
      </div>

      <div className="mt-4 grid grid-cols-3 gap-3 text-xs">
        <div className="bg-zinc-50 border border-zinc-100 rounded-md p-3"><div className="flex items-center gap-1.5 font-semibold text-zinc-700 mb-1"><Zap className="h-3 w-3" /> Prix</div><div className="text-zinc-500">« 19,90 € », « €19.90 », « 1 299,00 » : tous les formats sont compris</div></div>
        <div className="bg-zinc-50 border border-zinc-100 rounded-md p-3"><div className="flex items-center gap-1.5 font-semibold text-zinc-700 mb-1"><Zap className="h-3 w-3" /> Image</div><div className="text-zinc-500">data-ec-image, première image de la carte ou og:image</div></div>
        <div className="bg-zinc-50 border border-zinc-100 rounded-md p-3"><div className="flex items-center gap-1.5 font-semibold text-zinc-700 mb-1"><Zap className="h-3 w-3" /> Panier</div><div className="text-zinc-500">Conservé entre les pages (localStorage), vidé après l'envoi au checkout</div></div>
      </div>
    </Card>
  );
}
